import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ExamSurfaceDisclaimer } from '@/components/ExamSurfaceDisclaimer';
import { Alert } from '@/components/ui/Alert';
import { buttonClassName } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { LoadingLine } from '@/components/ui/States';
import { AdminApiError, fetchGenerations } from '@/features/admin-content/admin-api';
import { GenerationList } from '@/features/admin-content/GenerationList';
import { StaffOnly } from '@/features/admin-content/StaffOnly';
import { useCan, useCurrentUser } from '@/features/admin-content/use-current-user';
import { BRAND } from '@/lib/brand';
import { authClient } from '@/lib/auth-client';

export const Route = createFileRoute('/admin/content/')({
  component: AdminContentListPage,
});

function AdminContentListPage() {
  const navigate = useNavigate();
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const meQuery = useCurrentUser();
  const canReview = useCan('content.admin');
  const canGenerate = useCan('content.generate');

  const listQuery = useQuery({
    queryKey: ['admin', 'generations'],
    enabled: canReview,
    queryFn: () => fetchGenerations(),
  });

  if (sessionPending || meQuery.isPending) {
    return <LoadingLine>Loading</LoadingLine>;
  }

  if (!session) {
    void navigate({ to: '/sign-in' });
    return null;
  }

  if (!canReview) {
    return <StaffOnly />;
  }

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageHeader
          eyebrow="Workspace"
          title="Content generation"
          description="Review AI-generated reading passages before they go into the pilot bank."
        />
        {canGenerate ? (
          <Link to="/admin/content/new" className={buttonClassName()}>
            Start a new run
          </Link>
        ) : null}
      </div>

      <ExamSurfaceDisclaimer text={BRAND.disclaimer} />

      <Card padding="lg">
        {listQuery.isPending ? (
          <LoadingLine>Loading runs</LoadingLine>
        ) : listQuery.isError ? (
          <Alert
            tone="danger"
            title={
              listQuery.error instanceof AdminApiError
                ? 'We could not load the generation runs.'
                : 'Something went wrong.'
            }
          />
        ) : (
          <GenerationList items={listQuery.data.items} />
        )}
      </Card>
    </div>
  );
}
